import React, { useState, useMemo } from "react";
import {
  Box, Typography, MenuItem, Accordion, AccordionSummary, AccordionDetails, 
  Stack, RadioGroup, FormControlLabel, Radio, Checkbox, Select, FormControl,
  Paper, Button, OutlinedInput, Chip, TextField
} from "@mui/material";
import { 
  ExpandMore, AssignmentOutlined, 
  TextFields, Numbers, CalendarToday, 
  Subject, CheckBox, Edit, 
  ArrowDropDown, ArrowDropDownCircle,
  QuestionAnswer
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import CustomInput from "../../../../../../components/CustomForm/CustomInput";
import CustomDate from "../../../../../CustomForm/CustomDate";
import CustomRichText from "../../../../../../components/CustomForm/CustomRichText";
import { SignatureFieldBase64 } from "../../../../../CustomForm";
import { blobToBase64 } from "../../../../../../utils/blob";
import { submitForm } from "../../../../../../api/services/carePathWay";
import { QK } from "../../../../../../base/qk";
import ConfirmModal from "../../../../../ConfirmModal";

const CarePlan = ({ sections, responses, handleChange, fullData, clientID }) => {
  const [expanded, setExpanded] = useState(true);
  const [openConfirmSave, setOpenConfirmSave] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation((payload) => submitForm(payload), {
    onSuccess: () => {
      enqueueSnackbar("Care strategy updated", { variant: "success" });
      queryClient.invalidateQueries([QK.carePlan]);
      setOpenConfirmSave(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to update care strategy", { variant: "error" });
    }
  });

  const handleFinalSubmit = () => {
    if (!fullData?.id) return;

    const payload = { 
      form: fullData.id, 
      responses: responses, 
      client: clientID 
    };
    
    mutation.mutate(fullData.submission?.id 
      ? { id: fullData.submission.id, ...payload, method: "update" } 
      : { ...payload, method: "create" }
    );
  };

  const hasChanges = useMemo(() => {
    const initial = fullData?.submission?.responses || {};
    const carePlanFieldKeys = new Set();

    sections?.forEach(section => {
      section.fields?.forEach(field => {
        carePlanFieldKeys.add(`cp_field_${field.id}`);
        const addBranches = (bs) => bs?.forEach(b => {
          carePlanFieldKeys.add(`cp_field_${b.id}`);
          if (b.branches) addBranches(b.branches);
        });
        addBranches(field.branches);
      });
    });

    for (let key of carePlanFieldKeys) {
      const val1 = initial[key];
      const val2 = responses[key];

      if (Array.isArray(val1) || Array.isArray(val2)) {
        if (JSON.stringify(val1 || []) !== JSON.stringify(val2 || [])) return true;
        continue;
      }

      const normalize = (v) => (v === null || v === undefined ? "" : String(v).trim());
      if (normalize(val1) !== normalize(val2)) return true;
    }
    return false;
  }, [sections, fullData, responses]);

  const handleAccordionChange = (panelId) => (e, isExpanded) => setExpanded(isExpanded ? panelId : false);

  const handleSignatureChange = async (fieldId, blob) => {
    const key = `cp_field_${fieldId}`; 
    const base64 = blob ? await blobToBase64(blob) : null;
    handleChange(key, base64);
  };

  const getFieldIcon = (fieldType, level) => {
    const iconProps = {
      sx: { 
        fontSize: level > 0 ? 16 : 18, 
        color: level > 0 ? '#6366f1' : '#4f46e5',
        mr: 1,
        flexShrink: 0
      }
    };

    switch(fieldType) {
      case 'singleline':
        return <TextFields {...iconProps} />;
      case 'number':
        return <Numbers {...iconProps} />;
      case 'date':
        return <CalendarToday {...iconProps} />;
      case 'multiline':
        return <Subject {...iconProps} />;
      case 'checkbox':
        return <CheckBox {...iconProps} />;
      case 'signature':
        return <Edit {...iconProps} />;
      case 'select':
        return <ArrowDropDown {...iconProps} />;
      case 'multiselect':
        return <ArrowDropDownCircle {...iconProps} />;
      default:
        return <QuestionAnswer {...iconProps} />;
    }
  };

  const renderFieldWithBranches = (field, level = 0) => {
    const fieldKey = `cp_field_${field.id}`;
    const currentValue = responses[fieldKey] ?? (
        field.field_type === "multiselect" ? [] : 
        field.field_type === "checkbox" ? false : ""
    );

    const mainLabel = field.main_answer_label || "Yes";
    const triggerLabel = field.branch_condition || "No";

    return (
      <React.Fragment key={field.id}>
        <Box sx={{ width: '100%', mb: 1 }}>
          <Box sx={{ 
            ml: level * 4, 
            p: 1.5, 
            borderLeft: level > 0 ? "4px solid #6366f1" : "none", 
            bgcolor: level > 0 ? 'rgba(99, 102, 241, 0.02)' : 'transparent', 
            borderRadius: 1.5,
            mb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {getFieldIcon(field.field_type, level)}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '0.8rem'
                }}
              >
                {field.label}
              </Typography>
            </Box>
            
            {field.branches && field.branches.length > 0 ? (
              <RadioGroup row value={currentValue} onChange={(e) => handleChange(fieldKey, e.target.value)}>
                <FormControlLabel 
                  value={mainLabel} 
                  control={<Radio size="small" />} 
                  label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{mainLabel}</Typography>} 
                />
                <FormControlLabel 
                  value={triggerLabel} 
                  control={<Radio size="small" />} 
                  label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{triggerLabel}</Typography>} 
                />
              </RadioGroup>
            ) : (
              <Box sx={{ 
                maxHeight: '300px',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f5f9',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#cbd5e1',
                  borderRadius: '4px',
                },
              }}>
                {field.field_type === "singleline" && (
                  <CustomInput 
                    fullWidth 
                    size="small" 
                    value={currentValue} 
                    onChange={(e) => handleChange(fieldKey, e.target.value)} 
                    sx={{ 
                      mb: 1,
                      '& .MuiInputBase-input': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                )}
                {field.field_type === "number" && (
                  <CustomInput 
                    fullWidth 
                    type="number" 
                    size="small" 
                    value={currentValue} 
                    onChange={(e) => handleChange(fieldKey, e.target.value)} 
                    sx={{ 
                      mb: 1,
                      '& .MuiInputBase-input': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                )}
                {field.field_type === "date" && (
                  <CustomDate 
                    value={currentValue} 
                    onChange={(e) => handleChange(fieldKey, e.target.value)} 
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                )}
                {field.field_type === "multiline" && (
                  <CustomRichText
                    value={currentValue}
                    onChange={(value) => handleChange(fieldKey, value)}
                    placeholder="Write your response here..."
                  />
                )}
                {field.field_type === "checkbox" && (
                  <FormControlLabel 
                    control={
                      <Checkbox 
                        size="small" 
                        checked={Boolean(currentValue)} 
                        onChange={(e) => handleChange(fieldKey, e.target.checked)} 
                      />
                    } 
                    label={<Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>Confirm/Acknowledge</Typography>} 
                  />
                )}
                {field.field_type === "signature" && (
                  <SignatureFieldBase64 
                    value={currentValue} 
                    onChange={(b) => handleSignatureChange(field.id, b)} 
                  />
                )}
                {field.field_type === "select" && (
                  <FormControl fullWidth size="small">
                    <Select 
                      value={currentValue} 
                      onChange={(e) => handleChange(fieldKey, e.target.value)}
                      sx={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        '& .MuiSelect-select': {
                          fontSize: '0.8rem',
                          padding: '8px 12px'
                        }
                      }}
                    >
                      {field.options?.map((opt, i) => (
                        <MenuItem key={i} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {field.field_type === "multiselect" && (
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={currentValue || []}
                      onChange={(e) => handleChange(fieldKey, e.target.value)}
                      input={<OutlinedInput size="small" sx={{ fontSize: '0.8rem' }} />}
                      renderValue={(selected) => (
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 0.5,
                          maxHeight: '100px',
                          overflowY: 'auto',
                        }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" sx={{ fontSize: '0.7rem' }} />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 200,
                            overflowY: 'auto',
                            '& .MuiMenuItem-root': {
                              fontSize: '0.8rem'
                            }
                          }
                        }
                      }}
                    >
                      {field.options?.map((opt, i) => (
                        <MenuItem key={i} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {currentValue === triggerLabel && field.branches?.map(branch => renderFieldWithBranches(branch, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ pb: hasChanges ? 12 : 2 }}>
      {sections?.map((section) => (
        <Accordion 
          key={section.id} 
          expanded={expanded === `panel_${section.id}`} 
          onChange={handleAccordionChange(`panel_${section.id}`)} 
          sx={{ 
            mb: 2, 
            borderRadius: '12px !important', 
            '&:before': { display: 'none' }, 
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '0.8rem',
                fontWeight: 600
              }
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <AssignmentOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                {section.title}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#fff', p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {section.fields?.map(field => renderFieldWithBranches(field))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {hasChanges && (
        <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 1200 }}>
          <Paper elevation={4} sx={{ p: 1, pl: 3, pr: 1.2, borderRadius: "50px", bgcolor: "#0f172a", display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #334155' }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, letterSpacing: 1, fontSize: '0.7rem' }}>
              UNSAVED CHANGES
            </Typography>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => setOpenConfirmSave(true)}
              disabled={mutation.isLoading}
              sx={{ 
                bgcolor: "#fff", 
                color: "#000", 
                borderRadius: "50px", 
                px: 4, 
                py: 0.5, 
                fontWeight: 600, 
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': { bgcolor: '#f1f5f9' } 
              }}
            >
              {mutation.isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </Paper>
        </Box>
      )}

      <ConfirmModal
        show={openConfirmSave}
        handleClose={() => setOpenConfirmSave(false)}
        handleConfirm={handleFinalSubmit}
        title="Confirm Save"
        body="Are you sure you want to update the care plan strategy?"
        isLoading={mutation.isLoading}
      />
    </Box>
  );
};

export default CarePlan;